import { NormalizedSchema } from 'normalizr';
import { NormalizedData } from 'store/schemas';
export interface PartialEnterprise {
  id: string;
  name: string;
  employeesCount: number;
}

/**
 * Reference https://kentcdodds.com/blog/application-state-management-with-react
 */
export const IS_FETCHING = 'IS_FETCHING';
export const SET_ENTERPRISES = 'SET_ENTERPRISES';
export const FAILED = 'FAILED';
export const ENTERPRISE_ID_FILTER = 'ENTERPRISE_ID_FILTER';
export const CLEAR_FILTERS = 'CLEAR_FILTERS';
export interface IsFetchingActionType {
  type: typeof IS_FETCHING;
}
export interface SetEnterprisesActionType {
  type: typeof SET_ENTERPRISES;
  payload: NormalizedSchema<{
    enterprises: NormalizedData<PartialEnterprise>;
  }, string[]>;
}
export interface FailedActionType {
  type: typeof FAILED;
  error: any;
}
export interface EnterpriseIdFilterActionType {
  type: typeof ENTERPRISE_ID_FILTER;
  enterpriseId: string | undefined;
}
export interface ClearFiltersActionType {
  type: typeof CLEAR_FILTERS;
}
export type OrganizationFilterActionType = IsFetchingActionType | SetEnterprisesActionType | FailedActionType | EnterpriseIdFilterActionType | ClearFiltersActionType;
export interface OrganizationFiltersState {
  isFetching: boolean;
  loaded: boolean;
  enterprisesIds: string[] | null;
  enterpriseId: string | null | undefined;
  enterprisesById: Record<string, PartialEnterprise> | null;
}
export const FilterId = 'enterpriseFilterId';

// The persisted enterprise filter is keyed per organization. A single global
// key let a filter chosen under one organization ride along to another the
// same admin manages; the backend now (correctly) rejects a cross-organization
// `?enterpriseId=` with a 403, so an un-namespaced key turned a stale filter
// into a broken page. Scoping the key to the organization keeps each
// dashboard's filter to itself. Persistence lives in the provider, which holds
// the live organizationId; the reducer stays a pure state transition.
export const getFilterStorageKey = (organizationId: string) => `${FilterId}:${organizationId}`;
export const organizationFiltersReducer = (state: OrganizationFiltersState, action: OrganizationFilterActionType) => {
  switch (action.type) {
    case IS_FETCHING:
      {
        return {
          ...state,
          isFetching: true
        };
      }
    case SET_ENTERPRISES:
      {
        const enterprisesById = action.payload.entities.enterprises;
        // Drop a persisted filter that is not part of this organization's tree,
        // so a stale id (e.g. carried over from another organization) never
        // reaches the API and the filter label never shows another org's name.
        // Tested against the id list, not by indexing enterprisesById: an id such as
        // 'constructor' resolves to an inherited function there and would survive as a
        // filter the organization never returned
        const enterpriseId = state.enterpriseId && !action.payload.result.includes(state.enterpriseId) ? null : state.enterpriseId;
        return {
          ...state,
          enterprisesIds: action.payload.result,
          enterprisesById,
          enterpriseId,
          loaded: true
        };
      }
    case FAILED:
      {
        return {
          ...state,
          isFetching: false,
          loaded: false
        };
      }
    case ENTERPRISE_ID_FILTER:
      {
        return {
          ...state,
          enterpriseId: action.enterpriseId
        };
      }
    case CLEAR_FILTERS:
      {
        return {
          ...state,
          enterpriseId: null
        };
      }
    default:
      {
        throw new Error('Unsupported action type');
      }
  }
};
export const setEnterpriseFilter = (enterpriseId: string | undefined): EnterpriseIdFilterActionType => ({
  type: ENTERPRISE_ID_FILTER,
  enterpriseId
});